import { motion, AnimatePresence } from 'framer-motion';
import { Check, AlertCircle, CheckCircle, Eye, EyeOff, KeyRound, Loader2, MessageCircle, Mail } from 'lucide-react';
import { memo, useState, useMemo } from 'react';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import { SUPPORT } from '@/lib/constants';
import { suggestEmailFix } from '@/lib/emailTypo';

interface AuthFormProps {
  email: string;
  password: string;
  loading: boolean;
  /**
   * True quando a tentativa revelou que ja existe conta com este email e a
   * senha e que nao bateu. Troca o banner laranja de "use o email da compra"
   * pelo banner que manda ir no Forgot password.
   */
  accountExists?: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

// Premium input with sophisticated states
//
// 17/09/2026 — o formulario era invisivel para gerenciador de senha. Inspecao do
// DOM em producao: name, id, autocomplete, enterkeyhint e aria-label TODOS null
// nos dois campos. Chaveiro do iPhone, Google e 1Password nao tinham como saber
// o que preencher, e a mae que salvou a senha digitava de novo de cabeca. O
// ForgotPasswordModal deste mesmo app ja fazia certo (autoComplete="one-time-code").
const PremiumInput = memo(function PremiumInput({
  type,
  value,
  onChange,
  placeholder,
  label,
  disabled,
  error,
  success,
  autoFocus = false,
  id,
  name,
  autoComplete,
  enterKeyHint,
}: {
  type: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  disabled?: boolean;
  error?: string;
  success?: boolean;
  autoFocus?: boolean;
  id: string;
  name: string;
  autoComplete: string;
  enterKeyHint?: 'next' | 'go' | 'done';
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === 'password';
  const inputType = isPassword && showPassword ? 'text' : type;

  const hasValue = value.length > 0;
  const errorId = `${id}-error`;

  return (
    <div>
      {/* Label FIXA acima do campo, como Spotify, e nao flutuante.
          A flutuante sumia quando o campo estava vazio, deixando so o
          placeholder — e placeholder desaparece no instante em que ela comeca a
          digitar. Quem nao tem familiaridade com formulario fica sem saber o
          que aquele campo queria. Esta fica na tela o tempo todo. */}
      <label
        htmlFor={id}
        className={`mb-1.5 block text-[15px] font-semibold ${
          error ? 'text-red-700 dark:text-red-400' : 'text-foreground'
        }`}
      >
        {label}
      </label>

      {/* Input container */}
      <div className="relative">
        {/* Input field.
            O foco antes era um <div> com blur-md e bg-white/30 ATRAS de um input
            de 5% de opacidade — o branco vazava pelo campo e o campo focado
            virava um bloco cinza chapado, lendo como desabilitado. Agora o realce
            e box-shadow POR FORA: o campo continua escuro e o texto legivel. */}
        <input
          id={id}
          name={name}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          enterKeyHint={enterKeyHint}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          placeholder={placeholder}
          className={`
            relative h-[56px] w-full rounded-xl px-4 pr-12
            bg-card
            text-base text-foreground
            placeholder:text-muted-foreground/70
            outline-none
            transition-[border-color,box-shadow] duration-200
            disabled:cursor-not-allowed disabled:opacity-50
            ${error
              ? 'border border-red-500/60 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.18)]'
              : 'border border-border focus:border-accent focus:shadow-[0_0_0_3px_hsl(var(--accent)/0.18)]'
            }
          `}
        />

        {/* Right side icons */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {/* Password toggle */}
          {isPassword && hasValue && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={
                showPassword
                  ? 'Hide password'
                  : 'Show password as plain text. Warning: this will display your password on the screen.'
              }
              className="p-2 text-muted-foreground transition-colors hover:text-foreground"
            >
              {showPassword ? (
                <EyeOff className="h-[18px] w-[18px]" />
              ) : (
                <Eye className="h-[18px] w-[18px]" />
              )}
            </button>
          )}

          {/* Status indicator */}
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                initial={{ scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-red-700 dark:text-red-400"
              >
                <AlertCircle className="w-4 h-4" />
              </motion.div>
            )}
            {success && !error && (
              <motion.div
                key="success"
                initial={{ scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="text-emerald-700 dark:text-emerald-400"
              >
                <Check className="w-4 h-4" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p id={errorId} role="alert" className="text-red-700 dark:text-red-400 text-[15px] mt-2 ml-1">
          {error}
        </p>
      )}
    </div>
  );
});

// Password strength indicator
const PasswordStrength = memo(function PasswordStrength({
  password
}: {
  password: string;
}) {
  const strength = useMemo(() => {
    if (password.length === 0) return { score: 0, label: '', color: '' };
    if (password.length < 6) return { score: 1, label: 'Too short', color: 'bg-red-500' };

    let score = 2;
    if (password.length >= 8) score++;
    if (password.length >= 10) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 3) return { score: 2, label: 'Fair', color: 'bg-yellow-500' };
    if (score <= 4) return { score: 3, label: 'Good', color: 'bg-emerald-500' };
    return { score: 4, label: 'Strong', color: 'bg-emerald-400' };
  }, [password]);

  if (password.length === 0) return null;

  return (
    <motion.div
      // decoracao-ok: a barra de força da senha só existe depois que ela começa
      // a digitar, e a entrada é a própria expansão de altura. Não há o que
      // tocar aqui, e sem a altura animada a linha salta na tela.
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-3"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <div className="flex-1 flex gap-1">
          {[1, 2, 3, 4].map((level) => (
            <div
              key={level}
              className={`
                h-1 flex-1 origin-left rounded-full transition-colors duration-200
                ${level <= strength.score ? strength.color : 'bg-border'}
              `}
            />
          ))}
        </div>
        <span className="text-[15px] font-medium text-muted-foreground">
          {strength.label}
        </span>
      </div>
    </motion.div>
  );
});

// Animated submit button
const SubmitButton = memo(function SubmitButton({
  loading,
  disabled,
  children,
}: {
  loading: boolean;
  disabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <motion.button
      type="submit"
      disabled={disabled || loading}
      whileTap={{ scale: 0.98 }}
      className={`
        relative min-h-[58px] w-full overflow-hidden rounded-xl
        text-[18px] font-bold
        transition-colors duration-200
        ${disabled
          ? 'cursor-not-allowed border border-border bg-card text-muted-foreground'
          : 'bg-foreground text-background hover:bg-foreground/95'
        }
      `}
    >
      {/* Loading shimmer */}
      {loading && (
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: '100%' }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="absolute inset-0 bg-gradient-to-r from-transparent via-black/10 to-transparent"
        />
      )}

      {/* O rotulo do botao nao passa mais por AnimatePresence: ali um frame
          perdido deixava o botao de entrar sem texto nenhum. */}
      <span className="relative z-10 flex items-center justify-center gap-2">
        {loading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Please wait...</span>
          </span>
        ) : (
          <span>{children}</span>
        )}
      </span>
    </motion.button>
  );
});


export const AuthForm = memo(function AuthForm({
  email,
  password,
  loading,
  accountExists = false,
  onEmailChange,
  onPasswordChange,
  onSubmit,
}: AuthFormProps) {
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Simple email validation
  const emailError = useMemo(() => {
    if (!email) return undefined;
    if (!email.includes('@')) return 'Enter a valid email';
    if (!email.includes('.')) return 'Enter a valid email';
    return undefined;
  }, [email]);

  const emailValid = email.length > 0 && !emailError;
  // Qualquer senha nao vazia habilita o botao. 6 caracteres e regra para CRIAR
  // senha, nao para digitar uma que ja existe — e como o formulario agora serve
  // aos dois casos, quem decide se o tamanho basta e o signUp, com uma mensagem
  // que diz o que fazer. Botao cinza que nao explica nada foi o que deixou gente
  // parada aqui ate 16/09/2026.
  const passwordValid = password.length > 0;
  const formValid = emailValid && passwordValid;

  // Sugestão de typo de domínio (01/08/2026). Toda semana o /reconcile cata
  // gente que pagou, digitou o domínio errado e caiu numa tela sem produto:
  // gmail.co, aol.vom, gmail.con. Aqui só SUGERE, nunca bloqueia, e some no
  // instante em que ela corrige. Bloquear um email legítimo por palpite de
  // distância de edição custaria uma venda.
  const emailSuggestion = useMemo(
    () => (emailValid ? suggestEmailFix(email) : null),
    [email, emailValid],
  );

  return (
    // Sem animacao de entrada: esta e a primeira tela, nao ha de onde deslizar.
    <div className="mx-auto flex min-h-full w-full max-w-[26rem] flex-col">
      {/* Content */}
      <div className="flex flex-1 flex-col px-6 pb-[max(env(safe-area-inset-bottom),1rem)] pt-[max(env(safe-area-inset-top),1.5rem)]">
        {/* Marca discreta e titulo grande, como Spotify e Uber fazem: o logo
            identifica, o titulo pergunta. Nada de promessa — ela ja pagou, nao
            precisa ser convencida de novo na porta. */}
        <div className="mb-6 flex flex-col items-center gap-3 pt-2">
          {/* 25/09/2026: o mesmo ícone da tela inicial (três folhas), no lugar do coração
              em degradê rosa que só existia aqui. Ela acabou de tocar nele para abrir. */}
          <img
            src="/brand/app-icon-192.png"
            alt=""
            width={44}
            height={44}
            className="h-11 w-11 rounded-[12px]"
          />
          <h1
            className="text-center font-bold leading-tight tracking-tight text-foreground"
            style={{ fontSize: 'clamp(1.6rem, 7.5vw, 2rem)' }}
          >
            Sign in to your app
          </h1>
        </div>

        {/* Account already exists: the sign-up bounced and we flipped to sign-in
            with the email kept. Mirrors the banner that already works on
            /welcome-hp, which is the only reason that page does not produce
            these tickets. */}
        {/* Um aviso de cada vez, no mesmo lugar, com o mesmo formato. O verde
            fica: e a unica cor da tela que significa "deu certo, e por aqui" e
            aparece so no momento em que ela precisa desse sinal. */}
        {accountExists ? (
          <div className="mb-5 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.07] p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="mt-0.5 h-[18px] w-[18px] shrink-0 text-emerald-700 dark:text-emerald-400" />
              <div>
                <p className="mb-1 text-[15px] font-semibold text-emerald-700 dark:text-emerald-300">
                  You already have an account
                </p>
                <p className="text-[15px] leading-relaxed text-foreground/70">
                  This email is registered, so the password just needs to match.
                  If you do not remember it, tap Forgot password below and we
                  send you a 6 digit code.
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* O laranja fica reservado para o que PEDE acao — o foco do campo e a
             sugestao de typo, que e um botao. Este aviso e passivo: com ele em
             laranja tambem, a tela tinha tres laranjas no mesmo palmo. */
          <div className="mb-5 rounded-2xl border border-border bg-card/60 p-4">
            <p className="text-[15px] leading-relaxed text-foreground/70">
              <span className="font-semibold text-foreground">Important:</span>{' '}
              Use the email address you paid with.
            </p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-4">
          <PremiumInput
            id="email"
            name="email"
            autoComplete="username"
            enterKeyHint="next"
            type="email"
            value={email}
            onChange={onEmailChange}
            placeholder="Enter your email"
            label="Email"
            disabled={loading}
            error={email.length > 3 ? emailError : undefined}
            success={emailValid}
            autoFocus
          />

          {emailSuggestion && (
            <button
              type="button"
              onClick={() => onEmailChange(emailSuggestion)}
              className="-mt-2 flex min-h-[48px] w-full items-center gap-2.5 rounded-xl border border-accent/30 bg-accent/[0.08] px-4 py-3 text-left transition-colors active:bg-accent/[0.14]"
            >
              <AlertCircle className="h-[18px] w-[18px] shrink-0 text-accent" />
              <span className="text-[15px] leading-snug text-foreground/85">
                Did you mean <span className="font-bold text-accent">{emailSuggestion}</span>?
                {' '}Use this one.
              </span>
            </button>
          )}

          <div>
            {/* id e autocomplete mudam com o modo, e isso e proposital: e assim
                que o gerenciador sabe se deve OFERECER a senha salva ou PROPOR
                uma nova. Como o formulario agora serve aos dois casos, fica em
                current-password: a maioria de quem chega aqui ja tem conta, e
                para quem esta criando o navegador oferece salvar do mesmo jeito
                depois do envio. */}
            <PremiumInput
              id="current-password"
              name="password"
              autoComplete="current-password"
              enterKeyHint="go"
              type="password"
              value={password}
              onChange={onPasswordChange}
              placeholder="Enter your password"
              label="Password"
              disabled={loading}
              success={passwordValid}
            />

            {/* A forca da senha so importa para quem esta criando conta, e isso
                so se sabe depois do envio. Aparece quando a senha ainda nao
                serviria para criar — que e quando o aviso ajuda. */}
            {password.length > 0 && password.length < 6 && (
              <>
                <PasswordStrength password={password} />
                <p className="mt-1.5 text-[15px] text-muted-foreground">
                  If you are creating your account now, use at least 6 characters.
                </p>
              </>
            )}
          </div>

          {/* Submit button */}
          <div className="pt-4">
            <SubmitButton loading={loading} disabled={!formValid}>
              Continue
            </SubmitButton>
          </div>
        </form>

        {/* Forgot password: sempre visivel. Antes so aparecia no modo "entrar",
            e quem tinha chegado pelo "Get Started" — a maioria dos casos de
            suporte — nao tinha essa saida na tela. */}
        {/* O socorro, agora UNICO e com presenca de botao. Era um link cinza
            competindo com o "Email me a link", que tinha o dobro do peso visual
            e levava ao mesmo lugar: entrar sem lembrar a senha. Sobrou este,
            que e o que funciona — o modal manda um codigo de 6 digitos e se
            envia sozinho quando o sexto cai. */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className={`flex min-h-[54px] w-full items-center justify-center gap-2.5 rounded-xl border text-[16px] font-semibold transition-colors ${
              accountExists
                ? 'border-emerald-600/40 bg-emerald-500/[0.08] text-emerald-700 dark:border-emerald-400/30 dark:text-emerald-300'
                : 'border-border bg-card text-foreground hover:bg-muted'
            }`}
          >
            <KeyRound className="h-[18px] w-[18px]" />
            Forgot your password?
          </button>
        </div>

        {/* O "Email me a link to sign in" saiu daqui em 20/09/2026, e a rota
            continua viva para quem ja tem um link no email.

            Ele resolvia o MESMO problema que o botao acima — nao consigo
            entrar — e resolvia com o mecanismo que este produto ja provou
            fragil: em 24/08 mediram 20 pedidos de link, 20 logins de maquina
            segundos depois e ZERO trocas de senha. Scanner de email corporativo
            abre todo link da mensagem, e o nosso e de uso unico. Foi por isso
            que o codigo de 6 digitos nasceu.

            O codigo funciona: 77 pessoas pediram em 90 dias e 67 entraram, 87%.
            O link nunca foi instrumentado, entao nada justificava ele ocupar um
            botao de 54px com separador proprio ao lado do caminho principal. */}

        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          isOpen={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
          initialEmail={email}
        />

        {/* Spacer */}
        <div className="min-h-6 flex-1" />

        {/* Ajuda com o mesmo peso e o mesmo formato da tela de entrada: rotulo
            escrito por extenso e dois botoes de 48px que dao para ler e acertar.
            Antes eram dois icones de 13px espremidos entre divisorias — quem
            trava aqui e exatamente quem menos enxerga um alvo desse tamanho. */}
        {/* A ajuda em UM caminho. Eram dois botoes lado a lado, e ela nao tem
            como escolher com informacao: ninguem sabe qual responde mais rapido.
            Agora o primeiro toque diz "quero falar com gente" e a escolha do
            canal vem depois, ja com a decisao dificil tomada. */}
        {helpOpen ? (
          <div className="rounded-2xl border border-border bg-card/50 p-3">
            <p className="mb-2.5 text-center text-[15px] text-muted-foreground">
              Sarah answers both. Pick what is easier for you.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => window.open(SUPPORT.getWhatsAppUrl(), '_blank')}
                className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-card text-[15px] font-semibold text-foreground transition-colors hover:bg-muted"
              >
                <MessageCircle className="h-[18px] w-[18px]" />
                WhatsApp
              </button>

              <button
                type="button"
                onClick={() => { window.location.href = `mailto:${SUPPORT.EMAIL}`; }}
                className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-card text-[15px] font-semibold text-foreground transition-colors hover:bg-muted"
              >
                <Mail className="h-[18px] w-[18px]" />
                Email
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="flex min-h-[48px] w-full items-center justify-center gap-2.5 rounded-2xl border border-border bg-card/50 text-[15px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <MessageCircle className="h-[18px] w-[18px]" />
            Stuck? Talk to a person
          </button>
        )}
      </div>
    </div>
  );
});
